/**
 * Responsibility: photograph a card, and show what the analysis made of it.
 *
 * Three modes, because they are three different jobs with different capture
 * requirements and different honest answers.
 *
 * **Identify** wants speed over resolution. The live camera at 12 pixels per
 * millimetre is ample to recognise a card and useless for grading it, so this
 * mode never reports condition at all.
 *
 * **Grade** wants resolution over speed, so it hands off to the phone's own
 * camera - roughly 45 px/mm against the live stream's 12. It walks through the
 * shots one at a time, because a protocol nobody follows produces measurements
 * nobody should trust.
 *
 * **Verify** is the instrument. It shows the detected boundary, the magnified
 * corners, and every measurement with its own reason for existing or not - so
 * a finding can be argued with. Five separate attempts at measuring whitening
 * produced confident, plausible, wrong numbers before this page existed; what
 * caught each one was looking at the overlay.
 */

import { useState } from 'react';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GradingIcon from '@mui/icons-material/Grading';
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { PageContainer } from '@kit/components';

import {
  hubApi,
  type IdentifiedCard,
  type IdentifyResult,
  type MarketResult,
  type ScanResult,
} from '../api/client';
import { useAddToCollection } from '../api/hooks/useMarket';
import { usePageState } from '../hooks/usePageState';
import { useAddHoldingWithTags } from '../api/hooks/useTags';
import { AddHoldingDialog } from '../components/collection/AddHoldingDialog';
import { MarketCardDialog } from '../components/market/MarketCardDialog';
import { GamePicker, type ScanGame } from '../components/scan/GamePicker';
import { LanguagePicker, type ScanLanguage } from '../components/scan/LanguagePicker';
import { IdentifyPanel } from '../components/scan/IdentifyPanel';
import { CameraCapture, type CaptureGuide } from '../components/scan/CameraCapture';
import { ScanFindings } from '../components/scan/ScanFindings';
import { VerifyPanel } from '../components/scan/VerifyPanel';

type Mode = 'identify' | 'grade' | 'verify';

/**
 * The directed capture protocol.
 *
 * Flat shots first because they carry centering, which is the measurement
 * most worth having and the only one that survives an uncontrolled photo.
 * Raking is last because it needs the room lights changed, and asking for
 * that first loses people.
 */
const GRADE_STEPS: (CaptureGuide & { face: 'front' | 'back' })[] = [
  {
    face: 'front',
    shape: 'card',
    title: 'Front, flat',
    instruction:
      'Fill the frame. Phone parallel to the card, no flash. Diffuse overhead light, matte white or grey background, and no hard shadow at the card edge.',
  },
  {
    face: 'back',
    shape: 'card',
    title: 'Back, flat',
    instruction: 'Same again for the back. This is the one that carries centering and whitening.',
  },
  {
    face: 'front',
    shape: 'corner',
    title: 'Front corners',
    instruction:
      'Fill the frame with one corner at a time. Getting closer is what buys resolution — a corner filling the frame is about 90 px/mm.',
  },
  {
    face: 'front',
    shape: 'card',
    title: 'Front, raking light',
    instruction:
      'Overheads OFF. One lamp low and to the side, about 20–30°. The phone torch will not work — it sits beside the lens and gives flat light.',
  },
];

export function ScanPage() {
  const [mode, setMode] = useState<Mode>('identify');
  // **On by default, because that is the job.** Identifying a collection
  // is a hundred cards one after another and both hands are holding one;
  // a button to press between each is the slow part of the loop.
  const [autoScan, setAutoScan] = useState(true);
  const [face, setFace] = useState<'front' | 'back'>('back');
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Identification is a different question from grading and answers with
  // a different shape, so it keeps its own result rather than being bent
  // into the measurement one.
  const [named, setNamed] = useState<IdentifyResult | null>(null);
  //: The last automatic scan saw no card - the desk, a hand, a blur.
  const [emptyFrame, setEmptyFrame] = useState(false);
  // A preference rather than a step in this sitting: somebody scanning a
  // Pokemon shoebox today is scanning one tomorrow, so it outlives the
  // tab. Pokemon is the default because it is the only layout verified
  // at both ends of its printing history.
  const [game, setGame] = usePageState<ScanGame>('scan.game', 'pokemon', 'durable');
  // Defaults to English because that is the only language the readers
  // have been scored on, and a default nobody changes should be the
  // one that works. It is one tap to widen.
  const [language, setLanguage] = usePageState<ScanLanguage>(
    'scan.language',
    'english',
    'durable',
  );
  const [adding, setAdding] = useState<MarketResult | null>(null);
  const [opened, setOpened] = useState<MarketResult | null>(null);
  const add = useAddToCollection();
  const addWithTags = useAddHoldingWithTags();

  /** A scanned candidate, in the shape the rest of the app passes around. */
  const asMarketCard = (card: IdentifiedCard): MarketResult =>
    ({
      key: card.product_key,
      name: card.name,
      set_name: card.set_name,
      image_url: card.image_url,
    }) as MarketResult;

  async function identify(file: File, byItself = false) {
    setBusy(true);
    setError(null);
    try {
      const answer = await hubApi.identifyCard(file, game, language);
      // **An empty frame does not erase a good answer.** The camera
      // fires on whatever is in front of it, and between two cards that
      // is a desk. Losing a shortlist somebody is still reading because
      // the lens glanced at a placemat is worse than showing it a
      // moment longer, so the miss is reported beside it instead.
      //
      // Only for shots the camera took itself. A photograph somebody
      // chose is an instruction, and a chosen file that is already a
      // tight crop legitimately reports no card outline - suppressing
      // that answer would look like the upload being ignored.
      const nothingThere = answer.candidates.length === 0 || (byItself && !answer.card_found);
      if (nothingThere && named !== null) {
        setEmptyFrame(true);
      } else {
        setNamed(answer);
        setEmptyFrame(false);
      }
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : String(problem));
    } finally {
      setBusy(false);
    }
  }

  async function send(file: File, wantOverlay: boolean, forFace: 'front' | 'back') {
    setBusy(true);
    setError(null);
    try {
      setResult(await hubApi.scan(file, forFace, wantOverlay));
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : String(problem));
    } finally {
      setBusy(false);
    }
  }

  const identifyGuide: CaptureGuide = {
    shape: 'card',
    title: 'Line the card up',
    instruction:
      'Fill the outline. Any plain background works — it is measured, not assumed. Sharp matters more than bright.',
  };

  return (
    <PageContainer>
      <Stack spacing={2} sx={{ maxWidth: 780 }}>
        <Tabs value={mode} onChange={(_, v: Mode) => { setMode(v); setResult(null); }}>
          <Tab value="identify" label="Identify" icon={<CenterFocusStrongIcon />} iconPosition="start" />
          <Tab value="grade" label="Grade" icon={<GradingIcon />} iconPosition="start" />
          <Tab value="verify" label="Verify" icon={<FactCheckIcon />} iconPosition="start" />
        </Tabs>

        {mode === 'identify' && (
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Identification only. Condition is deliberately not reported from a live
              camera frame — at roughly 12 px/mm it would be a guess dressed up as a
              measurement.
            </Alert>
            <GamePicker game={game} onChange={setGame} disabled={busy} />
            <LanguagePicker language={language} onChange={setLanguage} disabled={busy} />
            <CameraCapture
              guide={identifyGuide}
              mode="live"
              busy={busy}
              auto={autoScan}
              onCapture={(f, byItself) => void identify(f, byItself)}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  size="small"
                  checked={autoScan}
                  onChange={(event) => setAutoScan(event.target.checked)}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Scan whenever the frame settles, and again when the card changes
                </Typography>
              }
            />
            {emptyFrame && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Nothing card-shaped in the last frame. The answer below is the card before it.
              </Typography>
            )}
            <Box sx={{ mt: 2 }}>
              <IdentifyPanel
                result={named}
                busy={busy}
                onAdd={(card) => setAdding(asMarketCard(card))}
                onOpen={(card) => setOpened(asMarketCard(card))}
              />
            </Box>
          </Paper>
        )}

        {mode === 'grade' && (
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
              {GRADE_STEPS.map((s, i) => (
                <Chip
                  key={s.title}
                  size="small"
                  label={`${i + 1}. ${s.title}`}
                  color={i === step ? 'primary' : 'default'}
                  variant={i === step ? 'filled' : 'outlined'}
                  onClick={() => setStep(i)}
                />
              ))}
            </Stack>
            <CameraCapture
              guide={GRADE_STEPS[step]}
              // The phone's own camera, for the resolution corners and
              // surface need. See CameraCapture for why the live stream
              // cannot do this job on an iPhone.
              mode="native"
              busy={busy}
              onCapture={(f) => {
                void send(f, true, GRADE_STEPS[step].face);
                setStep((s) => Math.min(s + 1, GRADE_STEPS.length - 1));
              }}
            />
          </Paper>
        )}

        {mode === 'verify' && (
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1">Verify</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a card and see exactly what the analysis did: where it decided the
              card edge was, the four corners magnified, and a ring around anything it
              flagged. If the dashed boundary is wrong, every measurement inside it is
              wrong too — that is the first thing to check.
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={face}
              onChange={(_, v: 'front' | 'back' | null) => v && setFace(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="back">Back</ToggleButton>
              <ToggleButton value="front">Front</ToggleButton>
            </ToggleButtonGroup>
            <CameraCapture
              guide={{
                shape: 'card',
                title: `Upload a ${face}`,
                instruction: 'Any photo — a listing image is fine. Overlays come back with the result.',
              }}
              mode="native"
              busy={busy}
              onCapture={(f) => void send(f, true, face)}
            />
          </Paper>
        )}

        {busy && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        {result && mode === 'grade' && <ScanFindings result={result} showCondition />}
        {result && mode === 'verify' && <VerifyPanel result={result} />}

        {!result && !busy && (
          <Box sx={{ opacity: 0.6 }}>
            <Typography variant="caption">
              Nothing is stored. Images are measured in memory and discarded; only the
              measurements come back.
            </Typography>
          </Box>
        )}
      </Stack>

      {/* The same two dialogs the search page uses. A scanned card and a
          searched one are the same card, and a second way to add one is a
          second thing to keep in step. */}
      <MarketCardDialog
        card={opened}
        onClose={() => setOpened(null)}
        onAdd={(card) => {
          setOpened(null);
          setAdding(card);
        }}
      />
      <AddHoldingDialog
        card={adding}
        busy={add.isPending}
        onClose={() => setAdding(null)}
        onAdd={(body, tagIds) => {
          addWithTags.mutate({ body, tagIds });
          setAdding(null);
        }}
      />
    </PageContainer>
  );
}
